// Two things RTK Query cannot carry, done the way the inbox already does its
// media upload (`hooks/useOutboundMedia.js`): a plain `fetch` with
// `authHeaders()`, so the authentication cannot drift from the slice's.
//
// ★ A FILE IS NOT A CACHE ENTRY AND A DOWNLOAD IS NOT A QUERY. `fetchBaseQuery`
// JSON-encodes bodies and parses responses; a FormData upload and a streamed
// CSV are neither. Forcing them through the slice means either fighting it or
// quietly corrupting the payload.

import { META_WHATSAPP_BASE, authHeaders } from 'api/metaWhatsappApi';

/**
 * Send an audience file up for parsing.
 *
 * The response is the row report — what was read, what could not be, what
 * matched an existing lead and what became a new one — and it is the whole
 * point of the call. The file itself is never stored server-side.
 *
 * @param {object}  input
 * @param {string}  input.campaignId
 * @param {File}    input.file
 * @param {object}  input.mapping       { phone, name, country, custom: { var: column } }
 * @param {string}  input.attestation   the words the uploader signed
 * @param {string} [input.defaultRegion] ISO2, for numbers with no country code
 * @returns {Promise<{ok: boolean, status: number, body: object}>}
 */
export async function uploadAudience({ campaignId, file, mapping, attestation, defaultRegion }) {
	const form = new FormData();
	form.append('file', file);
	// Multipart carries text, so the mapping travels as JSON and the server
	// parses it back — the same shape the wizard built.
	form.append('mapping', JSON.stringify(mapping ?? {}));
	form.append('attestation', attestation ?? '');
	if (defaultRegion) form.append('defaultRegion', defaultRegion);

	const res = await fetch(
		`${META_WHATSAPP_BASE}/campaigns/${encodeURIComponent(campaignId)}/audience/upload`,
		{
			method: 'POST',
			// ⚠ No content-type: the browser must set the multipart boundary
			// itself. Naming it here produces a body the server cannot parse.
			headers: authHeaders(),
			body: form,
		},
	);

	const body = await res.json().catch(() => ({}));
	return { ok: res.ok, status: res.status, body };
}

/**
 * Download a campaign's recipients as CSV.
 *
 * Fetched rather than linked, because the export is an authenticated route and
 * a bare `<a href>` cannot carry the bearer token — the same reason the inbox
 * fetches media instead of linking it. The object URL is revoked immediately;
 * the browser has already copied the blob by the time the click resolves.
 */
export async function downloadRecipients({ campaignId, campaignName, state, problems = false }) {
	const params = new URLSearchParams();
	// M10. The error report is the same export answering a narrower question:
	// every state that means the message did not arrive, with the columns that
	// say why. `state` is ignored when it is asked for — the point is that all
	// four problem states come in one file.
	if (problems) params.set('only', 'problems');
	else if (state) params.set('state', state);

	const res = await fetch(
		`${META_WHATSAPP_BASE}/campaigns/${encodeURIComponent(campaignId)}/recipients/export?${params}`,
		{ headers: authHeaders() },
	);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		return { ok: false, status: res.status, body };
	}

	const blob = await res.blob();
	const url = URL.createObjectURL(blob);
	const safeName = String(campaignName || 'campaign').replace(/[^\w-]+/g, '_').slice(0, 40);

	const link = document.createElement('a');
	link.href = url;
	link.download = `${safeName}-${problems ? 'problems' : 'recipients'}.csv`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);

	return { ok: true, status: res.status };
}
